import { useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { usePollingContext } from 'src/providers/PollingContextProvider';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { getTrafficV2Treatment } from 'src/utils/app';
import { ATLAS_SUBTABS, ATLAS_TABS } from 'src/components/AtlasV2Components/AtlasV2Sidebar/useAtlasV2Routes';
import { useOptimisticPendingSegmentsStore } from 'src/zustandStores/Atlas/useOptimisticPendingSegmentsStore';

const PROCESSING_SEGMENTS_POLLING_KEY = 'PROCESSING_SEGMENTS_POLL';

/**
 * Global segment-processing poller. Mount exactly ONCE near the app root via
 * {@link SegmentProcessingPoller}. It polls `GetProcessingSegments` while there is
 * work to track and fires the "Your segment was successfully created." snackbar
 * when a tracked segment finishes — independent of which page the user is on.
 *
 * Polling runs while either:
 *  - the user has optimistically pending segments (they just created/edited one), or
 *  - a consumer registered via {@link useSegmentsPagePolling} is mounted (the segments
 *    list page, which needs live processing status even on a fresh load).
 */
export const useSegmentProcessingPoller = () => {
  const divisionId = useAppSelector((state) => state.user.session?.divisionId);
  const retailerId = useAppSelector((state) => state.retailer?.id);
  const { showSnackbar } = useSnackbar();
  const { startPolling, stopPolling } = usePollingContext();
  const { beaconNavigator } = useBeaconNavigator();

  const optimisticPendingSegments = useOptimisticPendingSegmentsStore((state) => state.optimisticPendingSegments);
  const pollSubscriberCount = useOptimisticPendingSegmentsStore((state) => state.pollSubscriberCount);
  const setProcessingSegments = useOptimisticPendingSegmentsStore((state) => state.setProcessingSegments);
  const getOptimisticPendingSegmentIds = useOptimisticPendingSegmentsStore(
    (state) => state.getOptimisticPendingSegmentIds
  );
  const removeOptimisticPendingSegment = useOptimisticPendingSegmentsStore(
    (state) => state.removeOptimisticPendingSegment
  );

  const previousProcessingSegmentsRef = useRef<string[]>([]);

  const fetchProcessingSegments = useCallback(async () => {
    try {
      const response = await axios.post('/api/atlas/GetProcessingSegments', {
        divisionId,
        retailerId
      });

      if (response.data && Array.isArray(response.data)) {
        const currentProcessingSegments = response.data as string[];
        const previous = previousProcessingSegmentsRef.current;
        const optimisticPending = getOptimisticPendingSegmentIds();

        // Find segments that completed processing (were in previous but not in current)
        const completedSegments = previous.filter((segmentId) => !currentProcessingSegments.includes(segmentId));

        // Also check for optimistically pending segments that we previously saw processing
        // and that are no longer in the API response.
        const optimisticCompleted = optimisticPending.filter(
          (segmentId) => !currentProcessingSegments.includes(segmentId) && previous.includes(segmentId)
        );

        const allCompletedSegments = [...new Set([...completedSegments, ...optimisticCompleted])];

        if (allCompletedSegments.length > 0) {
          allCompletedSegments.forEach((segmentId) => {
            removeOptimisticPendingSegment(segmentId);
          });

          showSnackbar({
            type: 'success',
            showButton: true,
            buttonProps: {
              text: 'View Segment',
              onClick: () => {
                beaconNavigator.setPathname(`/segment/${allCompletedSegments[0]}`).addSearchParams({
                  tab: ATLAS_TABS.SUMMARY_V2,
                  subtab: ATLAS_SUBTABS.KEY_METRICS
                });

                beaconNavigator.go();
              }
            },
            message: 'Your segment was successfully created.',
            timeout: 3000
          });
        }

        setProcessingSegments(currentProcessingSegments);

        // Update previous ref to include both API and still-pending optimistic segments.
        // Exclude segments we just marked completed, otherwise the stale `optimisticPending`
        // snapshot (captured before removeOptimisticPendingSegment ran) leaks them back into
        // the ref and the next poll re-detects them as completed — firing the snackbar twice.
        const remainingOptimisticPending = optimisticPending.filter(
          (segmentId) => !allCompletedSegments.includes(segmentId)
        );
        previousProcessingSegmentsRef.current = [
          ...new Set([...currentProcessingSegments, ...remainingOptimisticPending])
        ];
      }
    } catch (error) {
      console.error('Error fetching processing segments:', error);
    }
  }, [
    divisionId,
    retailerId,
    showSnackbar,
    beaconNavigator,
    getOptimisticPendingSegmentIds,
    removeOptimisticPendingSegment,
    setProcessingSegments
  ]);

  // Keep a stable callback identity for the poller so changes to `fetchProcessingSegments`
  // (new closures) don't tear down and re-register the polling interval on every render.
  const fetchRef = useRef(fetchProcessingSegments);
  fetchRef.current = fetchProcessingSegments;
  const pollOnce = useCallback(() => fetchRef.current(), []);

  const trafficV2Treatment = getTrafficV2Treatment();
  const trafficV2Enabled = trafficV2Treatment === 'both' || trafficV2Treatment === 'on';
  const hasPendingWork = optimisticPendingSegments.size > 0;
  const shouldPoll = Boolean(
    trafficV2Enabled && divisionId && retailerId && (hasPendingWork || pollSubscriberCount > 0)
  );

  useEffect(() => {
    if (!shouldPoll) {
      return undefined;
    }

    // Start each polling session from a clean baseline so stale data from a previous
    // session can't be misread as a freshly-completed segment.
    previousProcessingSegmentsRef.current = [];

    try {
      startPolling(PROCESSING_SEGMENTS_POLLING_KEY, pollOnce, {
        interval: 5000,
        leading: true,
        shouldThrow: false
      });
    } catch (e) {
      console.error('Polling already started');
    }

    return () => stopPolling(PROCESSING_SEGMENTS_POLLING_KEY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPoll, divisionId, retailerId]);
};

/**
 * Read-only access to the segments currently processing (API-reported ∪ optimistic),
 * for rendering pending state in lists. Does not trigger polling on its own.
 */
export const useProcessingSegments = (): string[] => {
  const processingSegments = useOptimisticPendingSegmentsStore((state) => state.processingSegments);
  const optimisticPendingSegments = useOptimisticPendingSegmentsStore((state) => state.optimisticPendingSegments);

  return useMemo(
    () => [...new Set([...processingSegments, ...Array.from(optimisticPendingSegments)])],
    [processingSegments, optimisticPendingSegments]
  );
};

/**
 * For pages (the segments list) that need processing status to stay live while
 * visible: registers a poll subscriber for the lifetime of the component and
 * returns the merged processing segments.
 */
export const useSegmentsPagePolling = (): string[] => {
  const incrementPollSubscribers = useOptimisticPendingSegmentsStore((state) => state.incrementPollSubscribers);
  const decrementPollSubscribers = useOptimisticPendingSegmentsStore((state) => state.decrementPollSubscribers);

  useEffect(() => {
    incrementPollSubscribers();
    return () => decrementPollSubscribers();
  }, [incrementPollSubscribers, decrementPollSubscribers]);

  return useProcessingSegments();
};

/**
 * Mountable wrapper for {@link useSegmentProcessingPoller}. Render once globally.
 */
export const SegmentProcessingPoller = (): null => {
  useSegmentProcessingPoller();
  return null;
};
